/**
 * Attachment Viewer Modal
 *
 * Full-screen modal for viewing attachments with navigation and sharing
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight, MoreHorizontal, Mail, MessageSquare, Download, ImageDown, Trash2 } from 'lucide-react';
import { TransformWrapper, TransformComponent, type ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { useToast } from '@/contexts/ToastContext';
import { stampMediaAsNew } from '@/lib/mediaMetadata';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Attachment {
  path: string;
  name: string;
  bucket?: string;
  contentType?: string;
  url?: string;
}

interface AttachmentViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  attachments: Attachment[];
  attachmentUrls: Record<number, string>;
  initialIndex: number;
  onDownload?: (url: string, name: string) => void;
  onShare?: (type: 'sms' | 'email' | 'download', attachment: Attachment, url: string) => void;
  /** Starts the delete flow for an attachment (the parent owns the confirmation modal). */
  onDelete?: (attachment: Attachment) => void;
  /** Whether the current user may delete this attachment. Delete is hidden when it returns false. */
  canDelete?: (attachment: Attachment) => boolean;
}

export default function AttachmentViewerModal({
  isOpen,
  onClose,
  attachments,
  attachmentUrls,
  initialIndex,
  onDownload,
  onShare,
  onDelete,
  canDelete
}: AttachmentViewerModalProps) {
  const { showSuccess, showError: showErrorToast } = useToast();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Sync currentIndex when initialIndex changes (e.g., opening modal with different attachment)
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, isOpen]);

  // Reset zoom when switching attachments or reopening the modal
  useEffect(() => {
    transformRef.current?.resetTransform(0);
    setIsZoomed(false);
  }, [currentIndex, isOpen]);

  // The list shrinks under us when an attachment is deleted from this modal.
  // Holding the same index shows the next attachment; past the end we fall back
  // to the new last one, and with nothing left the modal has nothing to show.
  useEffect(() => {
    if (!isOpen) return;
    if (attachments.length === 0) {
      onClose();
    } else if (currentIndex > attachments.length - 1) {
      setCurrentIndex(attachments.length - 1);
    }
  }, [attachments.length, currentIndex, isOpen]);

  const currentAttachment = attachments[currentIndex];
  const currentUrl = attachmentUrls[currentIndex];

  const isImage = currentAttachment?.contentType?.startsWith('image/') ||
    /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(currentAttachment?.name || '');
  const isVideo = currentAttachment?.contentType?.startsWith('video/') ||
    /\.(mp4|mov|avi|webm|mkv|m4v|hevc|3gp|3g2|mts|ts|ogv)$/i.test(currentAttachment?.name || '');

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : attachments.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < attachments.length - 1 ? prev + 1 : 0));
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') onClose();
  };

  // Swipe-to-navigate on mobile — active only when not zoomed and a single finger is used
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isZoomed || e.touches.length !== 1 || attachments.length <= 1) {
      touchStartRef.current = null;
      return;
    }
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || isZoomed) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // Horizontal intent: ≥50px and more horizontal than vertical
    if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) handlePrevious();
      else handleNext();
    }
  };

  const handleDownloadClick = async () => {
    if (!currentUrl) return;

    try {
      const response = await fetch(currentUrl);
      const originalBlob = await response.blob();
      const fileName = currentAttachment.name || `attachment-${currentIndex + 1}`;
      // Re-stamp date metadata so the saved file is sorted as "new" in the
      // user's gallery instead of next to the original capture date.
      const stampedBlob = await stampMediaAsNew(
        originalBlob,
        fileName,
        currentAttachment.contentType,
      );
      const blobUrl = window.URL.createObjectURL(stampedBlob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);

      if (onDownload) {
        onDownload(currentUrl, currentAttachment.name);
      }
    } catch (error) {
      console.error('Download failed:', error);
      window.open(currentUrl, '_blank');
    }
  };

  const handleSaveToCameraRoll = async () => {
    if (!currentUrl) return;

    try {
      const response = await fetch(currentUrl);
      const blob = await response.blob();
      const fileName = currentAttachment.name || `attachment-${currentIndex + 1}`;

      // Ensure the blob has the correct MIME type for images/videos
      const mimeType = blob.type || currentAttachment.contentType || 'application/octet-stream';
      // Re-stamp date metadata so the file appears as a fresh capture rather
      // than being sorted next to the original photo in the camera roll.
      const typedBlob = await stampMediaAsNew(blob, fileName, mimeType);

      // Try Web Share API with file sharing. File.lastModified defaults to
      // Date.now(), which Android MediaStore uses as a fallback when EXIF
      // is absent — combined with the EXIF/atom rewrite above this makes
      // the saved asset appear "new" on both iOS and Android.
      if (navigator.share && navigator.canShare) {
        const file = new File([typedBlob], fileName, { type: mimeType });
        const shareData = { files: [file] };

        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          showSuccess('Saved', 'File shared successfully');
          return;
        }
      }

      // Fallback: open the blob in a new tab so the user can long-press to save
      // This works more reliably than the download attribute on iOS Safari/PWA
      const blobUrl = window.URL.createObjectURL(typedBlob);
      window.open(blobUrl, '_blank');
      showSuccess('Opened', 'Long-press the image to save to your camera roll');

      // Clean up after a delay to allow the new tab to load
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } catch (error: any) {
      // AbortError means user cancelled the share sheet — not an error
      if (error?.name === 'AbortError') return;

      console.error('Save to camera roll failed:', error);
      showErrorToast('Save Failed', 'Could not save the file. Please try again.');
      // Last resort: open the original URL in a new tab
      window.open(currentUrl, '_blank');
    }
  };

  const handleShareClick = (type: 'sms' | 'email' | 'download') => {
    if (type === 'download') {
      handleDownloadClick();
    } else if (onShare) {
      onShare(type, currentAttachment, currentUrl);
    }
  };

  // Attach keyboard listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown as any);
      return () => document.removeEventListener('keydown', handleKeyDown as any);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[95vw] sm:max-w-xl md:max-w-2xl p-0 gap-0 overflow-hidden h-[80vh] flex flex-col bg-black/95 border-0 rounded-[15px]">
        {/* Header with counter and close button — in flex flow on mobile so the image area sizes below it; absolute overlay on desktop */}
        <div className="relative md:absolute md:top-0 md:left-0 md:right-0 z-50 flex items-center justify-between p-4 flex-shrink-0">
          <div className="bg-gray-800/80 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <span className="text-white text-sm font-medium">
              {currentIndex + 1} / {attachments.length}
            </span>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="w-9 h-9 p-0 rounded-full bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/80 text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Main content area with image/video */}
        <div className="relative flex flex-col items-center justify-center w-full flex-1 min-h-0">
          {/* Previous button — hidden on mobile (swipe replaces it) */}
          {attachments.length > 1 && (
            <Button
              onClick={handlePrevious}
              variant="ghost"
              size="sm"
              className="hidden md:flex absolute left-4 z-40 w-10 h-10 p-0 rounded-full bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/80 text-white"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
          )}

          {/* Attachment display */}
          <div
            className="flex items-center justify-center w-full flex-1 px-0 md:px-20 pb-4 overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {currentUrl && isImage && (
              <TransformWrapper
                ref={transformRef}
                minScale={1}
                maxScale={6}
                initialScale={1}
                centerOnInit
                doubleClick={{ mode: 'toggle', step: 2 }}
                wheel={{ step: 0.15 }}
                pinch={{ step: 5 }}
                panning={{ velocityDisabled: true, disabled: !isZoomed }}
                limitToBounds
                onTransform={(_ref, state) => {
                  const zoomed = state.scale > 1.01;
                  setIsZoomed((prev) => (prev === zoomed ? prev : zoomed));
                }}
              >
                <TransformComponent
                  wrapperClass="!w-full !h-full !max-w-full !max-h-full"
                  contentClass="!w-full !h-full flex items-center justify-center"
                >
                  <img
                    src={currentUrl}
                    alt={currentAttachment.name}
                    className="max-w-full max-h-full object-contain select-none"
                    draggable={false}
                  />
                </TransformComponent>
              </TransformWrapper>
            )}
            {currentUrl && isVideo && (
              <video
                src={currentUrl}
                controls
                className="max-w-full max-h-full object-contain"
              />
            )}
            {(!currentUrl || (!isImage && !isVideo)) && (
              <div className="flex flex-col items-center justify-center gap-4 text-white">
                <div className="w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center">
                  <Download className="w-10 h-10" />
                </div>
                <p className="text-lg font-medium">{currentAttachment?.name || 'File'}</p>
                <Button
                  onClick={handleDownloadClick}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download File
                </Button>
              </div>
            )}
          </div>

          {/* Info bar below image/video */}
          <div className="w-full bg-gray-900/90 backdrop-blur-sm border-t border-gray-700/50 px-6 py-3 flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">
                {currentAttachment?.name || 'Attachment'}
              </p>
            </div>

            {/* Options dropdown menu — sharing plus delete */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-4 text-white hover:text-white hover:bg-transparent hover:border-white border border-transparent flex items-center gap-2 transition-all"
                >
                  <MoreHorizontal className="w-4 h-4" />
                  Options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={() => handleShareClick('sms')}
                  className="cursor-pointer"
                >
                  <MessageSquare className="w-4 h-4 mr-2 text-blue-600" />
                  <span>Share via SMS</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleShareClick('email')}
                  className="cursor-pointer"
                >
                  <Mail className="w-4 h-4 mr-2 text-green-600" />
                  <span>Share via Email</span>
                </DropdownMenuItem>
                {(isImage || isVideo) && (
                  <DropdownMenuItem
                    onClick={handleSaveToCameraRoll}
                    className="cursor-pointer"
                  >
                    <ImageDown className="w-4 h-4 mr-2 text-purple-600" />
                    <span>Save to Camera Roll</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => handleShareClick('download')}
                  className="cursor-pointer"
                >
                  <Download className="w-4 h-4 mr-2 text-gray-600" />
                  <span>Download</span>
                </DropdownMenuItem>
                {onDelete && currentAttachment && (!canDelete || canDelete(currentAttachment)) && (
                  <DropdownMenuItem
                    onClick={() => onDelete(currentAttachment)}
                    className="cursor-pointer text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Next button — hidden on mobile (swipe replaces it) */}
          {attachments.length > 1 && (
            <Button
              onClick={handleNext}
              variant="ghost"
              size="sm"
              className="hidden md:flex absolute right-4 z-40 w-10 h-10 p-0 rounded-full bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/80 text-white"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
